// ═══════════════════════════════════════════════════════════════════════════
// DOMAIN: sévérité d'un risque — règles pures (libellé + couleur)
// ═══════════════════════════════════════════════════════════════════════════
//
// La sévérité d'un risque = probabilité × impact (échelle 1-25 ; en pratique
// 1-16 avec des échelles 1-4). Deux règles métier PURES en dépendent, jusqu'ici
// piégées dans core.ts :
//
//   - severityLabel(s)        : seuils verbaux Faible / Modéré / Élevé / Critique.
//   - nearestSeverityColor()  : « snap » d'une sévérité vers la couleur de la
//     sévérité la plus proche présente dans la palette admin (évite les trous
//     gris sur les valeurs non listées : 5, 7, 10, 11, 13-15…).
//
// Extraites ici (juin 2026) pour être testables hors DOM (vitest) et rester la
// source de vérité unique. core.ts délègue désormais à ces fonctions :
// `sevLabel` ré-exporte `severityLabel`, et `sevColor` lit `window.__riskColors`
// puis appelle `nearestSeverityColor`. Aucun changement de comportement.

/**
 * Libellé verbal d'une sévérité (proba × impact). Seuils métier figés :
 *   ≥ 9 → Critique · ≥ 5 → Élevé · ≥ 3 → Modéré · sinon Faible.
 */
export function severityLabel(s: number): string {
  if (s >= 9) return 'Critique';
  if (s >= 5) return 'Élevé';
  if (s >= 3) return 'Modéré';
  return 'Faible';
}

/**
 * Couleur hex d'une sévérité d'après la palette admin `colorMap`
 * ({ [severity:number]: "#RRGGBB" }) :
 *   1. couleur exacte si la sévérité est mappée ;
 *   2. sinon « snap » vers la sévérité MAPPÉE la plus proche (la plus petite
 *      clé en cas d'égalité de distance, car on garde la première trouvée) ;
 *   3. `fallback` si la palette est vide (ou clé cible sans valeur).
 *
 * Fonction pure : aucune lecture de globals, la palette est injectée.
 */
export function nearestSeverityColor(
  colorMap: Record<number, string>,
  s: number,
  fallback: string,
): string {
  if (colorMap[s]) return colorMap[s] as string;
  const keys = Object.keys(colorMap)
    .map(Number)
    .filter((n) => !Number.isNaN(n));
  if (keys.length === 0) return fallback;
  let best = keys[0] as number;
  for (const k of keys) {
    if (Math.abs(k - s) < Math.abs(best - s)) best = k;
  }
  return colorMap[best] || fallback;
}
